import * as React from 'react'
import { Menubar as RadixMenubar } from 'radix-ui'
import { IconChevronRight } from '@posthog/icons'
import Link from 'components/Link'
import ScrollArea from './ScrollArea'
import KeyboardShortcut from 'components/KeyboardShortcut'
import { useApp } from '../../context/App'

// Types
export type MenuItemType = {
    type: 'item' | 'submenu' | 'separator'
    label?: string
    link?: string
    shortcut?: string | string[] // Support both string and array of keys
    disabled?: boolean
    icon?: React.ReactNode
    items?: MenuItemType[] // For submenus
    onClick?: () => void
    node?: React.ReactNode // Allow embedding a React node
    external?: boolean // Whether the link should open in a new window with external styling
    active?: boolean
    mobileDestination?: string | false // Mobile-specific destination URL or false to omit from mobile menu
}

export type MenuType = {
    trigger: React.ReactNode
    bold?: boolean
    items: MenuItemType[]
    mobileLink?: string // Direct link for the menu trigger on mobile
}

const RootClasses = 'flex gap-px py-0.5 h-full'
const TriggerClasses =
    'group flex select-none items-center justify-between gap-0.5 rounded px-1.5 py-0.5 text-[13px] leading-none text-primary outline-none data-[highlighted]:bg-accent hover:bg-accent-2 data-[state=open]:bg-accent'
const ItemClasses =
    'hover:bg-accent group relative flex h-[25px] select-none justify-between items-center rounded text-[13px] leading-none text-primary bg-primary outline-none data-[disabled]:pointer-events-none data-[disabled]:text-muted [&>span]:inline-flex [&>span]:w-full'
const SubTriggerClasses =
    'hover:bg-accent group relative flex h-[25px] select-none items-center rounded px-2.5 text-[13px] leading-none text-primary bg-primary outline-none data-[disabled]:pointer-events-none data-[disabled]:text-muted'
const ContentClasses =
    'bg-primary min-w-[180px] md:min-w-[220px] rounded-md p-[5px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[transform,opacity] [animation-duration:_400ms] [animation-timing-function:_cubic-bezier(0.16,_1,_0.3,_1)]'
const SeparatorClasses = 'm-[5px] h-px bg-border'
const ShortcutClasses =
    'ml-auto pl-5 group-hover:text-secondary group-data-[disabled]:text-muted data-[highlighted]:data-[state=open]:text-secondary group-data-[highlighted]:text-secondary'

// Helper to render menu item content (icon + label + chevron)
const MenuItemContent = (item: MenuItemType, forceIconIndent?: boolean) => {
    const iconContent = item.icon ? (
        <span className="mr-2 flex items-center">{item.icon}</span>
    ) : forceIconIndent ? (
        <span style={{ display: 'inline-block', width: 16, minWidth: 16 }} className="mr-2" />
    ) : null

    return (
        <>
            {iconContent}
            {item.label}
            <div className={ShortcutClasses}>
                <IconChevronRight className="size-4" />
            </div>
        </>
    )
}

// Process menu items for mobile display - truncate nesting to 2 levels max
const processMobileMenuItem = (item: MenuItemType): MenuItemType | null => {
    // Skip items marked for mobile omission
    if (item.mobileDestination === false) {
        return null
    }

    // If item has a mobile destination, convert submenu to simple link
    if (item.mobileDestination && item.type === 'submenu') {
        return {
            ...item,
            type: 'item' as const,
            link: item.mobileDestination,
            items: undefined, // Remove nested items on mobile
        }
    }

    // For submenus without explicit mobile destination, limit depth
    if (item.type === 'submenu' && item.items) {
        // If the submenu has a link, make it a simple link on mobile
        if (item.link) {
            return {
                ...item,
                type: 'item' as const,
                items: undefined,
            }
        }

        // Otherwise, process children but make them all leaf nodes
        if (Array.isArray(item.items)) {
            const processedItems = item.items
                .map((subItem: MenuItemType) => {
                    // Convert all nested submenus to simple items
                    if (subItem.type === 'submenu') {
                        return {
                            ...subItem,
                            type: 'item' as const,
                            link:
                                subItem.link ||
                                subItem.mobileDestination ||
                                subItem.items?.find((subItem) => !!subItem?.link)?.link ||
                                '#',
                            items: undefined,
                        }
                    }
                    return subItem
                })
                .filter(Boolean) as MenuItemType[]

            return {
                ...item,
                items: processedItems,
            }
        }
    }

    return item
}

const processMobileMenuItems = (items: MenuItemType[]): MenuItemType[] => {
    const processedItems: MenuItemType[] = []

    for (let i = 0; i < items.length; i++) {
        const item = items[i]

        // Skip items marked for mobile omission
        if (item.mobileDestination === false) {
            // Also skip the preceding separator if it exists
            if (processedItems.length > 0 && processedItems[processedItems.length - 1].type === 'separator') {
                processedItems.pop()
            }
            continue
        }

        const processed = processMobileMenuItem(item)
        if (processed) {
            processedItems.push(processed)
        }
    }

    return processedItems
}

// Components
const MenuItem: React.FC<{
    item: MenuItemType
    forceIconIndent?: boolean
    menuIndex: number
}> = ({ item, forceIconIndent, menuIndex }) => {
    if (item.type === 'separator') {
        return <RadixMenubar.Separator className={SeparatorClasses} />
    }

    if (item.node) {
        return (
            <RadixMenubar.Item className={ItemClasses} disabled={item.disabled} onClick={item.onClick}>
                {item.node}
            </RadixMenubar.Item>
        )
    }

    if (item.type === 'submenu' && item.items) {
        // If items is an array, render as before
        if (Array.isArray(item.items)) {
            const anyChildHasIcon = item.items.some((subItem) => !!subItem.icon)
            return (
                <RadixMenubar.Sub>
                    {item.link ? (
                        <Link
                            to={item.link}
                            state={{ newWindow: true }}
                            externalNoIcon={item.external}
                            className="no-underline"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <RadixMenubar.SubTrigger className={SubTriggerClasses}>
                                {MenuItemContent(item, forceIconIndent)}
                            </RadixMenubar.SubTrigger>
                        </Link>
                    ) : (
                        <RadixMenubar.SubTrigger className={SubTriggerClasses}>
                            {MenuItemContent(item, forceIconIndent)}
                        </RadixMenubar.SubTrigger>
                    )}
                    <RadixMenubar.Portal>
                        <RadixMenubar.SubContent className={ContentClasses} alignOffset={-5} data-scheme="primary">
                            <ScrollArea className="max-h-screen !overflow-y-auto">
                                {item.items.map((subItem, subIndex) => (
                                    <MenuItem
                                        key={`${subItem.link}-${subIndex}`}
                                        item={subItem}
                                        forceIconIndent={anyChildHasIcon}
                                        menuIndex={menuIndex}
                                    />
                                ))}
                            </ScrollArea>
                        </RadixMenubar.SubContent>
                    </RadixMenubar.Portal>
                </RadixMenubar.Sub>
            )
        }
        // If items is a React element, render it directly
        if (React.isValidElement(item.items)) {
            return (
                <RadixMenubar.Sub>
                    <RadixMenubar.SubTrigger className={SubTriggerClasses}>
                        {item.icon ? (
                            <span className="mr-2 flex items-center">{item.icon}</span>
                        ) : forceIconIndent ? (
                            <span style={{ display: 'inline-block', width: 16, minWidth: 16 }} className="mr-2" />
                        ) : null}
                        {item.label}
                        <div className={ShortcutClasses}>
                            <IconChevronRight className="size-4" />
                        </div>
                    </RadixMenubar.SubTrigger>
                    <RadixMenubar.Portal>
                        <RadixMenubar.SubContent className={ContentClasses} alignOffset={-5} data-scheme="primary">
                            {item.items}
                        </RadixMenubar.SubContent>
                    </RadixMenubar.Portal>
                </RadixMenubar.Sub>
            )
        }
    }

    return (
        <RadixMenubar.Item
            className={`${ItemClasses} ${item.active ? 'bg-accent' : ''}`}
            disabled={item.disabled}
            onClick={item.onClick}
        >
            {item.link ? (
                <Link
                    to={item.link}
                    state={{ newWindow: true }}
                    externalNoIcon={item.external}
                    className="w-full min-h-[25px] h-full px-2.5 flex items-center gap-2 no-underline text-primary"
                >
                    {item.icon ? (
                        item.icon
                    ) : forceIconIndent ? (
                        <span style={{ display: 'inline-block', width: 16, minWidth: 16 }} />
                    ) : null}
                    <span>{item.label}</span>
                </Link>
            ) : (
                <span className="px-2.5 flex w-full justify-between items-center gap-2">
                    <span className="flex-1 flex items-center gap-2">
                        {item.icon ? (
                            item.icon
                        ) : forceIconIndent ? (
                            <span style={{ display: 'inline-block', width: 16, minWidth: 16 }} />
                        ) : null}
                        <span>{item.label}</span>
                    </span>
                    {item.shortcut && (
                        <div className={`${ShortcutClasses} hidden md:block`}>
                            {Array.isArray(item.shortcut) ? (
                                <div className="flex items-center">
                                    {item.shortcut.map((key, index) => (
                                        <React.Fragment key={index}>
                                            <KeyboardShortcut text={key} size="xs" />
                                            {/* 
                                            {index < item.shortcut!.length - 1 && (
                                                <span className="text-muted text-xs">+</span>
                                            )}
                                             */}
                                        </React.Fragment>
                                    ))}
                                </div>
                            ) : (
                                <KeyboardShortcut text={item.shortcut} size="xs" />
                            )}
                        </div>
                    )}
                </span>
            )}
        </RadixMenubar.Item>
    )
}

export interface MenuBarProps {
    menus: MenuType[]
    className?: string
    customTriggerClasses?: string
    triggerAsChild?: boolean
}

const MenuBar: React.FC<MenuBarProps> = ({ menus, className, triggerAsChild, customTriggerClasses }) => {
    const { isMobile } = useApp()

    // Process menus for mobile if needed
    const processedMenus = React.useMemo(() => {
        if (!isMobile) return menus

        return menus.map((menu) => {
            // If menu has mobileLink, don't process items since they won't be shown
            if (menu.mobileLink) {
                return menu
            }

            return {
                ...menu,
                items: processMobileMenuItems(menu.items),
            }
        })
    }, [menus, isMobile])

    return (
        <RadixMenubar.Root data-scheme="tertiary" className={`${RootClasses} ${className || ''}`}>
            {processedMenus.map((menu, menuIndex) => {
                // On mobile, if menu has mobileLink, make it a direct link
                if (isMobile && menu.mobileLink) {
                    return (
                        <Link
                            key={menuIndex}
                            to={menu.mobileLink}
                            state={{ newWindow: true }}
                            className={`${TriggerClasses} ${menu.bold ? 'font-bold' : 'font-medium'} ${
                                customTriggerClasses || ''
                            }`}
                        >
                            {menu.trigger}
                        </Link>
                    )
                }

                return (
                    <RadixMenubar.Menu key={menuIndex} data-scheme="primary">
                        <RadixMenubar.Trigger
                            asChild={triggerAsChild}
                            className={`${triggerAsChild ? '' : TriggerClasses} ${
                                menu.bold ? 'font-bold' : 'font-medium'
                            } ${customTriggerClasses}`}
                        >
                            {menu.trigger}
                        </RadixMenubar.Trigger>
                        <RadixMenubar.Portal>
                            <RadixMenubar.Content
                                className={ContentClasses}
                                align="start"
                                sideOffset={5}
                                alignOffset={-3}
                                data-scheme="primary"
                            >
                                {menu.items.map((item, itemIndex) => (
                                    <MenuItem key={`${menuIndex}-${itemIndex}`} item={item} menuIndex={menuIndex} />
                                ))}
                            </RadixMenubar.Content>
                        </RadixMenubar.Portal>
                    </RadixMenubar.Menu>
                )
            })}
        </RadixMenubar.Root>
    )
}

export default MenuBar
